import { FC } from 'react';
import { getRenderingApiUrl } from 'infrastructure/pdf';
import { generatePdf, PdfGenerator } from 'framework/markdownEditor';
import useDocumentStorageService from 'domain/shared/documentStorage/documentStorageService';
import useCandidateOfferService from '../candidateOfferService';
import { useNavigate, useParams } from 'react-router-dom';
import { CONTRACT_TYPES, useCandidateUrl } from 'domain/shared/candidate';
import { OfferPdfPlaceholderValues } from '../types';

type JobOfferPlaceholderValues = OfferPdfPlaceholderValues & {
  acceptanceDate?: string;
};

interface JobOfferPdfGeneratorProps {
  offerId: string;
  editedContent: string;
  placeholderValues: JobOfferPlaceholderValues;
  firstName: string;
  fullName: string;
}

const JobOfferPdfGenerator: FC<JobOfferPdfGeneratorProps> = ({
  offerId,
  editedContent,
  placeholderValues,
  firstName,
  fullName,
}) => {
  const { uploadArtifactRaw } = useDocumentStorageService();
  const { commitGeneratedOfferDocument } = useCandidateOfferService();
  const navigate = useNavigate();
  const { candidateId } = useParams();
  const { getProfileUrl } = useCandidateUrl();

  const handleGeneratePdf = async () => {
    const { contractType, finalAmountGross, currency, office, probationAmountGross } = placeholderValues;

    const isEC = contractType === CONTRACT_TYPES.EC;
    const isB2B = contractType === CONTRACT_TYPES.B2B;

    const viewData = {
      candidateName: firstName,
      vacancySpecialization: placeholderValues.vacancySpecialization,
      acceptanceDate: placeholderValues.acceptanceDate,
      office,
      compensationDetails: [
        {
          contractType,
          isEC,
          isB2B,
          finalAmountGross,
          probationAmountGross,
          currency,
        },
      ],
    };
    const pdfBlob = await generatePdf(getRenderingApiUrl(), editedContent, viewData);
    const file = new File([pdfBlob], `job offer ${fullName || 'candidate'}.pdf`, { type: 'application/pdf' });
    const uploadResult = await uploadArtifactRaw({ file });
    const tempPath = uploadResult?.path || uploadResult?.id;

    if (!tempPath) throw new Error('Failed to upload generated offer document');

    await commitGeneratedOfferDocument(offerId, tempPath);
    navigate(getProfileUrl(candidateId));

    return pdfBlob;
  };

  return (
    <PdfGenerator
      onGeneratePdf={handleGeneratePdf}
      fileName={`job offer ${fullName}.pdf`}
      successMessage="Job offer PDF successfully generated"
    />
  );
};

export default JobOfferPdfGenerator;
